/**
 * “Use this account to buy from this shop” — shown after adding a product to the
 * cart from the community (see `contexts/BuyContext`).
 *
 * One tap creates/uses the shop's customer account from the **verified Google
 * email**, so the shop owner sees a real customer and the order belongs to an
 * account. The cart is untouched.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FiShoppingBag, FiX } from 'react-icons/fi';
import { useBuy } from '../../contexts/BuyContext';
import { useLanguage } from '../../i18n';
import { fullUrl } from '../../api';
import GoogleLogin from '../GoogleLogin';
import { BLUE } from './brand';

export default function BuyAccountModal() {
  const { t } = useLanguage();
  const { ask, closeAsk } = useBuy();
  if (!ask?.shop?.id) return null;

  const shop = ask.shop;
  const label = shop.shop_name || shop.username || t('shopNav');

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm anim-fade-in" onClick={closeAsk} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 shadow-2xl anim-modal overflow-hidden">
        <div className="px-6 pt-6 pb-4 text-center" style={{ background: BLUE }}>
          {shop.logo ? (
            <img src={fullUrl(shop.logo)} alt={label} className="w-14 h-14 mx-auto rounded-2xl object-cover shadow" />
          ) : (
            <span className="w-14 h-14 mx-auto rounded-2xl bg-white/20 text-white flex items-center justify-center">
              <FiShoppingBag className="w-7 h-7" />
            </span>
          )}
          <h2 className="mt-2 text-white font-extrabold text-base">{label}</h2>
          <p className="text-white/85 text-[11px] mt-0.5">@{shop.username || ''}</p>
          <button
            type="button"
            onClick={closeAsk}
            className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/15 text-white hover:bg-white/25"
            aria-label="Close"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          <p className="text-sm text-center text-gray-600 dark:text-gray-300">{t('buyAccountTitle')}</p>
          <p className="text-[11px] leading-relaxed text-center text-gray-400">{t('buyAccountNote')}</p>

          <GoogleLogin scope="customer" shopId={shop.id} label={t('buyAccountAction')} />

          <div className="flex items-center justify-between text-xs pt-1">
            <button type="button" onClick={closeAsk} className="font-semibold text-gray-500 hover:underline">
              {t('continueShopping')}
            </button>
            {shop.username && (
              <Link to={`/${shop.username}`} onClick={closeAsk} className="font-bold" style={{ color: BLUE }}>
                {t('visitShop')} →
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
